import dynamic from 'next/dynamic';
import * as React from 'react';
import styled from 'styled-components';

import { HeadingPlugin } from '../../../../slatePlugins/elements/heading/HeadingPlugin';
import { ParagraphPlugin } from '../../../../slatePlugins/elements/paragraph/ParagraphPlugin';
import { ExitBreakPlugin } from '../../../../slatePlugins/handlers/exit-break/ExitBreakPlugin';
import { SoftBreakPlugin } from '../../../../slatePlugins/handlers/soft-break/SoftBreakPlugin';
import { BoldPlugin } from '../../../../slatePlugins/marks/bold/BoldPlugin';
import { MARK_BOLD } from '../../../../slatePlugins/marks/bold/defaults';
import { MARK_ITALIC } from '../../../../slatePlugins/marks/italic/defaults';
import { ItalicPlugin } from '../../../../slatePlugins/marks/italic/ItalicPlugin';
import { MARK_STRIKETHROUGH } from '../../../../slatePlugins/marks/strikethrough/defaults';
import { StrikethroughPlugin } from '../../../../slatePlugins/marks/strikethrough/StrikethroughPlugin';
import { UnderlinePlugin } from '../../../../slatePlugins/marks/underline/UnderlinePlugin';
import { withMarks } from '../../../../slatePlugins/marks/withMarks';
import { withInlineVoid } from '../../../../slatePlugins/plugins/inline-void/withInlineVoid';
import { DEFAULTS_OPTIONS } from '../../../BlogEditor/editorDefaultOptions';
import { DivPlugin, withDivEditorChains } from '../../../BlogEditor/plugins/DivPlugin';
import { withDeserializeHTML } from '../../deserializers/html-deserializer/withDeserializeHTML';
import {
  DEFAULT_HIGHLIGHT_COLORS_LIST,
  ELEMENT_HIGHLIGHT,
} from '../../elements/highlight/defaults';
import { HighlightElementPlugin } from '../../elements/highlight/plugin';
import { withHighlightPlugin } from '../../elements/highlight/withHighlightPlugin';
import { IconPlugin } from '../../elements/icons/IconPlugin';
import { ELEMENT_NUMBERING } from '../../elements/numbering/defaults';
import { NumberingElementPlugin } from '../../elements/numbering/plugin';
import { withNumberingPlugin } from '../../elements/numbering/withNumberingPlugin';
import { ELEMENT_ROUNDED } from '../../elements/rounded/defaults';
import { RoundedElementPlugin } from '../../elements/rounded/plugin';
import { withRoundedPlugin } from '../../elements/rounded/withRoundedPlugin';
import { withBreakLinePlugin } from '../../handlers/break-line-no-style/withBreakLine';
import { SlateListPlugin } from '../../marks/list';
import { normalizeListItemOnPaste } from '../../marks/list/normalizeListItemOnPaste';
import { DEFAULT_TEXT_COLOR_LIST, MARK_TEXT_COLOR } from '../../marks/text-color/defaults';
import { TextColorPlugin } from '../../marks/text-color/TextColorPlugin';
import { MARK_UNDERLINE_COLOR } from '../../marks/underline-color/defaults';
import { UnderlineColorPlugin } from '../../marks/underline-color/UnderlineColorPlugin';
import { disable } from '../disable-item/withDisableToolbarItem';
import { SideToolbarItemStyle } from './SideToolbarItemStyle';
import { NoteElementPlugin } from '../../elements/note/plugin';
import { HighlightPlugin } from '../../../../slatePlugins/marks/highlight';

const IconToolbarButton = dynamic<any>(import('../../elements/icons/toolbar'), { ssr: false });
const ToolbarElement = dynamic<any>(
  import('../../../../slatePlugins/components/ToolbarElement/ToolbarElement').then(
    (mod) => mod.ToolbarElement
  ),
  { ssr: false }
);
const ToolbarMark = dynamic<any>(
  import('../../../../slatePlugins/components/ToolbarMark/ToolbarMark').then(
    (mod) => mod.ToolbarMark
  ),
  { ssr: false }
);
const ToolbarNumberingElement = dynamic<any>(
  import('../../elements/numbering/toolbar/ToolbarNumberingElement').then(
    (mod) => mod.ToolbarNumberingElement
  ),
  { ssr: false }
);
const ToolbarHighlightElement = dynamic<any>(
  import('../../elements/highlight/toolbar/ToolbarHighlightElement').then(
    (mod) => mod.ToolbarHighlightElement
  ),
  { ssr: false }
);
const ToolbarTextColorMark = dynamic<any>(
  import('../../marks/text-color/toolbar/ToolbarTextColorMark').then(
    (mod) => mod.ToolbarTextColorMark
  ),
  { ssr: false }
);
const ToolbarUnderlineColorMark = dynamic<any>(
  import('../../marks/underline-color/toolbar/ToolbarUnderlineColorMark').then(
    (mod) => mod.ToolbarUnderlineColorMark
  ),
  { ssr: false }
);
const ToolbarRoundedElement = dynamic<any>(
  import('../../elements/rounded/toolbar/ToolbarRoundedElement').then(
    (mod) => mod.ToolbarRoundedElement
  ),
  { ssr: false }
);

const IconContentAddNumber = dynamic<any>(
  import('@doltech/icons/IconContentAddNumber').then((mod) => mod.IconContentAddNumber),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatBold = dynamic<any>(
  import('@doltech/icons/IconContentFormatBold').then((mod) => mod.IconContentFormatBold),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatColor = dynamic<any>(
  import('@doltech/icons/IconContentFormatColor').then((mod) => mod.IconContentFormatColor),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatHeading1 = dynamic<any>(
  import('@doltech/icons/IconContentFormatHeading1').then((mod) => mod.IconContentFormatHeading1),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatHeading2 = dynamic<any>(
  import('@doltech/icons/IconContentFormatHeading2').then((mod) => mod.IconContentFormatHeading2),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatItalic = dynamic<any>(
  import('@doltech/icons/IconContentFormatItalic').then((mod) => mod.IconContentFormatItalic),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatNormalText = dynamic<any>(
  import('@doltech/icons/IconContentFormatNormalText').then(
    (mod) => mod.IconContentFormatNormalText
  ),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatStrike = dynamic<any>(
  import('@doltech/icons/IconContentFormatStrike').then((mod) => mod.IconContentFormatStrike),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentFormatUnderline = dynamic<any>(
  import('@doltech/icons/IconContentFormatUnderline').then((mod) => mod.IconContentFormatUnderline),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentHighlight = dynamic<any>(
  import('@doltech/icons/IconContentHighlight').then((mod) => mod.IconContentHighlight),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentLayoutList = dynamic<any>(
  import('@doltech/icons/IconContentLayoutList').then((mod) => mod.IconContentLayoutList),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentLayoutNum = dynamic<any>(
  import('@doltech/icons/IconContentLayoutNum').then((mod) => mod.IconContentLayoutNum),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconContentSmileMouthOpen = dynamic<any>(
  import('@doltech/icons/IconContentSmileMouthOpen').then((mod) => mod.IconContentSmileMouthOpen),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);
const IconRounded = dynamic<any>(
  import('@doltech/icons/IconRounded').then((mod) => mod.IconRounded),
  { ssr: false, loading: () => <span>&nbsp;</span> }
);

const StyledToolbarElement = styled(ToolbarElement)`
  ${SideToolbarItemStyle}
`;

const StyledToolbarMark = styled(ToolbarMark)`
  ${SideToolbarItemStyle}
`;

export const DEFAULTS_SIDE_TOOLBAR_ITEMS = {
  toolbar: (options = DEFAULTS_OPTIONS) => {
    return (
      <>
        <StyledToolbarElement type={options.p.type} icon={<IconContentFormatNormalText />} />

        <StyledToolbarMark type={MARK_BOLD} icon={<IconContentFormatBold />} />

        <disable.ToolbarElement
          disable={{
            onActiveElementType: [
              ELEMENT_ROUNDED,
              ELEMENT_HIGHLIGHT,
              ELEMENT_NUMBERING,
              options.h1.type,
            ],
          }}
          styles={SideToolbarItemStyle}
          type={options.h1.type}
          icon={<IconContentFormatHeading1 />}
        />

        <StyledToolbarMark type={MARK_ITALIC} icon={<IconContentFormatItalic />} />

        <disable.ToolbarElement
          disable={{
            onActiveElementType: [
              ELEMENT_ROUNDED,
              ELEMENT_HIGHLIGHT,
              ELEMENT_NUMBERING,
              options.h1.type,
            ],
          }}
          type={options.h2.type}
          icon={<IconContentFormatHeading2 />}
        />

        <StyledToolbarMark type={MARK_STRIKETHROUGH} icon={<IconContentFormatStrike />} />

        <disable.ToolbarList
          {...options.ul}
          disable={{
            onActiveElementType: [
              ELEMENT_ROUNDED,
              ELEMENT_HIGHLIGHT,
              ELEMENT_NUMBERING,
              options.h1.type,
            ],
          }}
          typeList={options.ul.type}
          icon={<IconContentLayoutList />}
        />

        <ToolbarUnderlineColorMark
          type={MARK_UNDERLINE_COLOR}
          styles={SideToolbarItemStyle}
          colors={DEFAULT_TEXT_COLOR_LIST}
          icon={<IconContentFormatUnderline />}
        />

        <disable.ToolbarList
          {...options.ol}
          disable={{
            onActiveElementType: [
              ELEMENT_ROUNDED,
              ELEMENT_HIGHLIGHT,
              ELEMENT_NUMBERING,
              options.h1.type,
            ],
          }}
          typeList={options.ol.type}
          icon={<IconContentLayoutNum />}
        />

        <ToolbarTextColorMark
          type={MARK_TEXT_COLOR}
          colors={DEFAULT_TEXT_COLOR_LIST}
          icon={<IconContentFormatColor />}
        />

        <IconToolbarButton icon={<IconContentSmileMouthOpen />} />

        <ToolbarHighlightElement
          disable={{
            onActiveElementType: [ELEMENT_ROUNDED, ELEMENT_NUMBERING, options.h1.type],
          }}
          type={ELEMENT_HIGHLIGHT}
          icon={<IconContentHighlight />}
          colors={DEFAULT_HIGHLIGHT_COLORS_LIST}
        />

        <ToolbarNumberingElement
          icon={<IconContentAddNumber />}
          disable={{
            onActiveElementType: [ELEMENT_ROUNDED, ELEMENT_HIGHLIGHT, options.h1.type],
          }}
        />

        <ToolbarRoundedElement
          disable={{
            onActiveElementType: [ELEMENT_HIGHLIGHT, ELEMENT_NUMBERING, options.h1.type],
          }}
          icon={<IconRounded />}
        />
      </>
    );
  },
};

export const useSideToolbarPlugins = ({
  options = DEFAULTS_OPTIONS,
  extraPlugins = [],
  extraEditorChains = [],
}) => {
  const plugins = React.useMemo(
    () => [
      // elements
      ...extraPlugins,
      ParagraphPlugin(options),
      HeadingPlugin(options),
      HighlightElementPlugin({
        colors: DEFAULT_HIGHLIGHT_COLORS_LIST,
      }),
      NumberingElementPlugin(),
      RoundedElementPlugin(),
      IconPlugin(),

      // text format
      BoldPlugin(options),
      ItalicPlugin(options),
      UnderlinePlugin(options),
      StrikethroughPlugin(options),
      TextColorPlugin(),
      UnderlineColorPlugin(),
      HighlightPlugin(),
      NoteElementPlugin(),
      DivPlugin({}),

      // utils
      SoftBreakPlugin({
        rules: [
          {
            hotkey: 'shift+enter',
          },
        ],
      }),
      ExitBreakPlugin({
        rules: [
          {
            hotkey: 'mod+enter',
          },
          {
            hotkey: 'mod+shift+enter',
            before: true,
          },
          {
            hotkey: 'enter',
            query: {
              start: true,
              end: true,
              allow: ['h3'],
            },
          },
        ],
      }),
      SlateListPlugin.plugin(),
    ],
    [options, extraPlugins]
  );

  const editorChains = React.useMemo(
    () => [
      ...extraEditorChains,
      withInlineVoid({ plugins }),
      withDeserializeHTML({ plugins, normalizeFragment: normalizeListItemOnPaste }), // This must be above any plugins handle copy/paste ( so it won't add default html handling )
      withBreakLinePlugin(), // this must ahead of list plugin ( so it won't override insert break behavior of list )
      withDivEditorChains(),
      SlateListPlugin.withPlugin(),
      withMarks(),
      withHighlightPlugin(),
      withNumberingPlugin(),
      withRoundedPlugin(),
    ],
    [extraEditorChains, plugins]
  );

  return { plugins, editorChains };
};
